import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getSettings, updateSettings, listChats, updateChat, discoverChats, getRivhitConfig, updateRivhitConfig, testRivhitConfig, discoverWhatsAppBridge, runOrderSuggestions, getWhatsAppPhoneLinkStatus, startWhatsAppPhoneLink, confirmWhatsAppPhoneLink, deleteWhatsAppPhoneLink } from '../api/client';
import { useRivhitData } from '../context/RivhitDataContext';
import { LanguageSwitcher, WhatsAppConnect } from '../components';

interface ChatConfig {
  id: string;
  name: string;
  chat_type: string;
  priority: string;
  is_monitored: boolean;
  is_order_analysis_group?: boolean;
  sensitivity: number;
  participants_count?: number;
  in_database?: boolean;
  last_order_suggestions_run_at?: string;
}

interface AppSettings {
  language: string;
  business_hours_start: string;
  business_hours_end: string;
  auto_create_reminders: boolean;
  chat_monitoring_enabled: boolean;
  debt_age_threshold_days: number;
  min_confidence_threshold: number;
  noise_sensitivity: number;
  high_priority_keywords: string[];
  priority_customers: string[];
  priority_suppliers: string[];
}

interface RivhitTestResult {
  success: boolean;
  message: string;
}

interface WhatsAppPhoneLinkStatus {
  verified: boolean;
  linked_phone_number?: string | null;
  linked_at?: string | null;
  pending: boolean;
  pending_phone_number?: string | null;
  pending_created_at?: string | null;
  pending_expires_at?: string | null;
}

const defaultSettings: AppSettings = {
  language: 'en',
  business_hours_start: '08:00',
  business_hours_end: '18:00',
  auto_create_reminders: true,
  chat_monitoring_enabled: true,
  debt_age_threshold_days: 60,
  min_confidence_threshold: 0.6,
  noise_sensitivity: 0.5,
  high_priority_keywords: [],
  priority_customers: [],
  priority_suppliers: [],
};

/* ── Paginated chat list sub-component ── */

interface ChatListProps {
  chats: ChatConfig[];
  chatType: 'group' | 'private';
  search: string;
  onSearchChange: (v: string) => void;
  page: number;
  onPageChange: (p: number) => void;
  pageSize: number;
  onChatUpdate: (id: string, data: Partial<ChatConfig>) => void;
  onRunAnalysis: (id: string) => void;
  runningAnalysisChatId: string | null;
  t: (key: string) => string;
}

function ChatListWithPagination({ chats, chatType, search, onSearchChange, page, onPageChange, pageSize, onChatUpdate, onRunAnalysis, runningAnalysisChatId, t }: ChatListProps) {
  const filtered = useMemo(() =>
    chats
      .filter(c => c.chat_type === chatType)
      .filter(c => !search || c.name?.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => (b.is_monitored ? 1 : 0) - (a.is_monitored ? 1 : 0) || a.name.localeCompare(b.name)),
    [chats, chatType, search]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  return (
    <div className="space-y-3">
      {/* Search */}
      <div className="relative">
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={chatType === 'group' ? '🔎 Search groups…' : '🔎 Search contacts…'}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
        />
        {search && (
          <button
            onClick={() => onSearchChange('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
          >✕</button>
        )}
      </div>

      {/* Summary */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">
          {search ? `${filtered.length} results` : `${filtered.length} ${chatType === 'group' ? 'groups' : 'contacts'}`}
          {' · '}{filtered.filter(c => c.is_monitored).length} monitored
        </span>
        {totalPages > 1 && (
          <span className="text-xs text-gray-400">
            Page {safePage} of {totalPages}
          </span>
        )}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <p className="text-sm text-gray-400 py-4 text-center">
          {search
            ? `No ${chatType === 'group' ? 'groups' : 'contacts'} matching "${search}"`
            : `No ${chatType === 'group' ? 'groups' : 'contacts'} registered yet.`}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wider">
                <th className="pb-2 pr-4">{chatType === 'group' ? 'Group' : 'Contact'}</th>
                <th className="pb-2 pr-4">Priority</th>
                <th className="pb-2 pr-4">Monitored</th>
                {chatType === 'group' && <th className="pb-2 pr-4">Order Analysis</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pageItems.map((chat) => (
                <tr key={chat.id} className={!chat.is_monitored ? 'opacity-50' : ''}>
                  <td className="py-2 pr-4 font-medium text-gray-900">
                    <div className="flex items-center gap-1.5">
                      <span>{chat.chat_type === 'group' ? '👥' : '👤'}</span>
                      <span className="truncate max-w-[200px]">{chat.name}</span>
                      {chat.participants_count && chat.participants_count > 1 && (
                        <span className="text-[10px] text-gray-400">({chat.participants_count})</span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 pr-4">
                    <select
                      value={chat.priority}
                      onChange={(e) => onChatUpdate(chat.id, { priority: e.target.value })}
                      className="rounded-md border border-gray-300 px-2 py-1 text-xs"
                    >
                      {['high', 'normal', 'low', 'muted', 'excluded'].map((p) => (
                        <option key={p} value={p}>{t(`conversations.priority.${p}`)}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2 pr-4">
                    <button
                      onClick={() => onChatUpdate(chat.id, { is_monitored: !chat.is_monitored })}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${chat.is_monitored ? 'bg-green-500' : 'bg-gray-300'}`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${chat.is_monitored ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </button>
                  </td>
                  {chatType === 'group' && (
                    <td className="py-2 pr-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onChatUpdate(chat.id, { is_order_analysis_group: !chat.is_order_analysis_group })}
                          className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
                            chat.is_order_analysis_group
                              ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                          }`}
                        >
                          {chat.is_order_analysis_group ? 'Enabled' : 'Off'}
                        </button>
                        <button
                          onClick={() => onRunAnalysis(chat.id)}
                          disabled={runningAnalysisChatId === chat.id || !chat.is_order_analysis_group}
                          className="rounded-md border border-gray-300 px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                        >
                          {runningAnalysisChatId === chat.id ? 'Running…' : 'Run now'}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 pt-1">
          <button
            onClick={() => onPageChange(1)}
            disabled={safePage === 1}
            className="px-2 py-1 text-xs rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          >«</button>
          <button
            onClick={() => onPageChange(safePage - 1)}
            disabled={safePage === 1}
            className="px-2 py-1 text-xs rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          >‹</button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1)
            .reduce<(number | '...')[]>((acc, p, idx, arr) => {
              if (idx > 0 && p - (arr[idx - 1]) > 1) acc.push('...');
              acc.push(p);
              return acc;
            }, [])
            .map((item, i) =>
              item === '...' ? (
                <span key={`dots-${i}`} className="px-1 text-xs text-gray-400">…</span>
              ) : (
                <button
                  key={item}
                  onClick={() => onPageChange(item)}
                  className={`px-2.5 py-1 text-xs rounded-md font-medium transition-colors ${
                    item === safePage
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >{item}</button>
              )
            )}
          <button
            onClick={() => onPageChange(safePage + 1)}
            disabled={safePage === totalPages}
            className="px-2 py-1 text-xs rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          >›</button>
          <button
            onClick={() => onPageChange(totalPages)}
            disabled={safePage === totalPages}
            className="px-2 py-1 text-xs rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          >»</button>
        </div>
      )}
    </div>
  );
}

export default function Settings() {
  const { t } = useTranslation();
  const { reloadAfterConfigChange } = useRivhitData();
  const [settings, setSettings] = useState<AppSettings>(defaultSettings);
  const [chats, setChats] = useState<ChatConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [discoverResults, setDiscoverResults] = useState<ChatConfig[] | null>(null);
  const [chatSearch, setChatSearch] = useState('');
  const [chatTab, setChatTab] = useState<'group' | 'private'>('group');
  const [groupPage, setGroupPage] = useState(1);
  const [contactPage, setContactPage] = useState(1);
  const PAGE_SIZE = 10;
  const [runningAnalysisChatId, setRunningAnalysisChatId] = useState<string | null>(null);

  // Rivhit integration state
  const [rivhitHasToken, setRivhitHasToken] = useState(false);
  const [rivhitHasCredentials, setRivhitHasCredentials] = useState(false);
  const [rivhitToken, setRivhitToken] = useState('');
  const [rivhitUsername, setRivhitUsername] = useState('');
  const [rivhitPassword, setRivhitPassword] = useState('');
  const [rivhitIdCompany, setRivhitIdCompany] = useState('');
  const [rivhitSaving, setRivhitSaving] = useState(false);
  const [rivhitSaved, setRivhitSaved] = useState(false);
  const [rivhitTesting, setRivhitTesting] = useState(false);
  const [rivhitTestResult, setRivhitTestResult] = useState<RivhitTestResult | null>(null);
  const [rivhitError, setRivhitError] = useState('');
  const [waConnected, setWaConnected] = useState(false);
  const [phoneLinkStatus, setPhoneLinkStatus] = useState<WhatsAppPhoneLinkStatus | null>(null);
  const [phoneNumberInput, setPhoneNumberInput] = useState('');
  const [phoneCodeInput, setPhoneCodeInput] = useState('');
  const [phoneLinkLoading, setPhoneLinkLoading] = useState(false);
  const [phoneLinkMessage, setPhoneLinkMessage] = useState('');
  const [phoneLinkError, setPhoneLinkError] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [settingsRes, chatsRes, rivhitRes, phoneLinkRes] = await Promise.all([
        getSettings(),
        listChats(false),
        getRivhitConfig().catch(() => null),
        getWhatsAppPhoneLinkStatus().catch(() => null),
      ]);
      const s = settingsRes.data?.settings ?? settingsRes.data ?? {};
      setSettings({ ...defaultSettings, ...s });
      setChats(chatsRes.data?.chats ?? chatsRes.data ?? []);
      if (rivhitRes?.data) {
        setRivhitHasToken(rivhitRes.data.has_token ?? false);
        setRivhitHasCredentials(rivhitRes.data.has_credentials ?? false);
      }
      if (phoneLinkRes?.data) {
        setPhoneLinkStatus(phoneLinkRes.data);
        setPhoneNumberInput(phoneLinkRes.data.pending_phone_number || phoneLinkRes.data.linked_phone_number || '');
      }
    } catch { /* use defaults */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings(settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } finally { setSaving(false); }
  };

  const handleRivhitSave = async () => {
    setRivhitSaving(true);
    setRivhitError('');
    setRivhitSaved(false);
    try {
      const payload: { api_token?: string; username?: string; password?: string; id_company?: string } = {};
      if (rivhitToken) payload.api_token = rivhitToken;
      if (rivhitUsername) payload.username = rivhitUsername;
      if (rivhitPassword) payload.password = rivhitPassword;
      if (rivhitIdCompany) payload.id_company = rivhitIdCompany;
      const res = await updateRivhitConfig(payload);
      setRivhitHasToken(res.data.has_token ?? false);
      setRivhitHasCredentials(res.data.has_credentials ?? false);
      setRivhitToken('');
      setRivhitUsername('');
      setRivhitPassword('');
      setRivhitIdCompany('');
      // Reload Rivhit data context with new keys
      await reloadAfterConfigChange();
      setRivhitSaved(true);
      setTimeout(() => setRivhitSaved(false), 4000);
    } catch {
      setRivhitError(t('settings.rivhit.error'));
    } finally {
      setRivhitSaving(false);
    }
  };

  const handleRivhitTest = async () => {
    setRivhitTesting(true);
    setRivhitError('');
    setRivhitTestResult(null);
    try {
      const payload: { api_token?: string; username?: string; password?: string; id_company?: string } = {};
      if (rivhitToken) payload.api_token = rivhitToken;
      if (rivhitUsername) payload.username = rivhitUsername;
      if (rivhitPassword) payload.password = rivhitPassword;
      if (rivhitIdCompany) payload.id_company = rivhitIdCompany;
      const res = await testRivhitConfig(payload);
      setRivhitTestResult({
        success: !!res.data?.success,
        message: res.data?.message || (res.data?.success ? t('settings.rivhit.testSuccess') : t('settings.rivhit.testFailed')),
      });
    } catch (err: any) {
      setRivhitTestResult({
        success: false,
        message: err?.response?.data?.detail || t('settings.rivhit.testFailed'),
      });
    } finally {
      setRivhitTesting(false);
    }
  };

  const refreshPhoneLinkStatus = useCallback(async () => {
    try {
      const res = await getWhatsAppPhoneLinkStatus();
      setPhoneLinkStatus(res.data);
      setPhoneNumberInput(res.data.pending_phone_number || res.data.linked_phone_number || '');
    } catch {
      /* ignore */
    }
  }, []);

  const handleStartPhoneLink = async () => {
    setPhoneLinkLoading(true);
    setPhoneLinkError('');
    setPhoneLinkMessage('');
    try {
      const res = await startWhatsAppPhoneLink({ phone_number: phoneNumberInput.trim() });
      setPhoneLinkStatus(res.data);
      setPhoneLinkMessage(t('settings.whatsappAssistant.verificationSent'));
    } catch (err: any) {
      setPhoneLinkError(err?.response?.data?.detail || t('settings.whatsappAssistant.sendError'));
    } finally {
      setPhoneLinkLoading(false);
    }
  };

  const handleConfirmPhoneLink = async () => {
    setPhoneLinkLoading(true);
    setPhoneLinkError('');
    setPhoneLinkMessage('');
    try {
      const res = await confirmWhatsAppPhoneLink({ phone_number: phoneNumberInput.trim(), code: phoneCodeInput.trim() });
      setPhoneLinkStatus(res.data);
      setPhoneCodeInput('');
      setPhoneLinkMessage(t('settings.whatsappAssistant.verifySuccess'));
    } catch (err: any) {
      setPhoneLinkError(err?.response?.data?.detail || t('settings.whatsappAssistant.verifyError'));
    } finally {
      setPhoneLinkLoading(false);
    }
  };

  const handleDeletePhoneLink = async () => {
    setPhoneLinkLoading(true);
    setPhoneLinkError('');
    setPhoneLinkMessage('');
    try {
      const res = await deleteWhatsAppPhoneLink();
      setPhoneLinkStatus(res.data);
      setPhoneCodeInput('');
      setPhoneNumberInput('');
      setPhoneLinkMessage(t('settings.whatsappAssistant.unlinkSuccess'));
    } catch (err: any) {
      setPhoneLinkError(err?.response?.data?.detail || t('settings.whatsappAssistant.unlinkError'));
    } finally {
      setPhoneLinkLoading(false);
    }
  };

  const handleChatUpdate = async (chatId: string, data: Partial<ChatConfig>) => {
    await updateChat(chatId, data);
    setChats((prev) => prev.map((c) => c.id === chatId ? { ...c, ...data } : c));
    // Also update discover results if open
    if (discoverResults) {
      setDiscoverResults((prev) => prev?.map((c) => c.id === chatId ? { ...c, ...data } : c) ?? null);
    }
  };

  const handleDiscover = async () => {
    setDiscovering(true);
    try {
      const groups: ChatConfig[] = await discoverWhatsAppBridge();

      // Register any new chats in the backend
      const newChats = groups.filter(g => !g.in_database);
      if (newChats.length > 0) {
        await discoverChats(newChats.map(c => ({
          id: c.id,
          name: c.name,
          chat_type: c.chat_type,
          participants_count: c.participants_count || 1,
          is_monitored: false,
          is_order_analysis_group: false,
        })));
      }

      setDiscoverResults(groups);
      // Refresh main chat list
      const chatsRes = await listChats(false);
      setChats(chatsRes.data?.chats ?? chatsRes.data ?? []);
    } catch (err) {
      console.error('Discover failed:', err);
    } finally {
      setDiscovering(false);
    }
  };

  const handleRunAnalysis = async (chatId: string) => {
    setRunningAnalysisChatId(chatId);
    try {
      await runOrderSuggestions(chatId);
      await fetchData();
    } catch (err) {
      console.error('Run analysis failed:', err);
    } finally {
      setRunningAnalysisChatId(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[40vh] text-gray-400">{t('common.loading')}</div>;
  }

  return (
    <div className="max-w-3xl mx-auto space-y-8 pb-24">
      <h1 className="text-2xl font-bold text-gray-900">{t('settings.title')}</h1>

      {/* General Settings */}
      <section className="rounded-lg border border-gray-200 bg-white p-5 space-y-5">
        <h2 className="text-lg font-semibold text-gray-900">{t('settings.general')}</h2>

        {/* Language */}
        <div className="flex items-center justify-between">
          <div>
            <label className="text-sm font-medium text-gray-700">{t('settings.language')}</label>
          </div>
          <LanguageSwitcher />
        </div>

        {/* Business hours */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('settings.businessHours')}</label>
          <div className="flex items-center gap-3">
            <input type="time" value={settings.business_hours_start} onChange={(e) => setSettings({ ...settings, business_hours_start: e.target.value })} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm" />
            <span className="text-gray-400">—</span>
            <input type="time" value={settings.business_hours_end} onChange={(e) => setSettings({ ...settings, business_hours_end: e.target.value })} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('settings.debtAgeThreshold')}</label>
          <input
            type="number"
            min={1}
            value={settings.debt_age_threshold_days}
            onChange={(e) => setSettings({
              ...settings,
              debt_age_threshold_days: Math.max(1, Number(e.target.value) || 1),
            })}
            className="w-32 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          />
          <p className="mt-1 text-xs text-gray-400">{t('settings.debtAgeThresholdDesc')}</p>
        </div>

        {/* Chat monitoring toggle */}
        <div className="flex items-center justify-between">
          <div>
            <label className="text-sm font-medium text-gray-700">{t('settings.chatMonitoring')}</label>
            <p className="text-xs text-gray-400">{t('settings.chatMonitoringDesc')}</p>
          </div>
          <button
            onClick={() => setSettings({ ...settings, chat_monitoring_enabled: !settings.chat_monitoring_enabled })}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.chat_monitoring_enabled ? 'bg-primary-600' : 'bg-gray-300'}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.chat_monitoring_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

      </section>

      {/* Rivhit Integration */}
      <section className="rounded-lg border border-gray-200 bg-white p-5 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('settings.rivhit.title')}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{t('settings.rivhit.description')}</p>
          </div>
          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${
            rivhitHasToken || rivhitHasCredentials
              ? 'bg-green-100 text-green-700'
              : 'bg-gray-100 text-gray-500'
          }`}>
            <span className={`h-1.5 w-1.5 rounded-full ${rivhitHasToken || rivhitHasCredentials ? 'bg-green-500' : 'bg-gray-400'}`} />
            {rivhitHasToken || rivhitHasCredentials ? t('settings.rivhit.connected') : t('settings.rivhit.notConfigured')}
          </span>
        </div>

        {/* Login credentials */}
        <div className="space-y-3">
          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.rivhit.username')}</label>
            <input
              type="text"
              value={rivhitUsername}
              onChange={(e) => setRivhitUsername(e.target.value)}
              placeholder={rivhitHasCredentials ? '••••••••' : t('settings.rivhit.usernamePlaceholder')}
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.rivhit.password')}</label>
            <input
              type="password"
              value={rivhitPassword}
              onChange={(e) => setRivhitPassword(e.target.value)}
              placeholder={rivhitHasCredentials ? '••••••••' : t('settings.rivhit.passwordPlaceholder')}
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
            />
          </div>

          {/* Company ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.rivhit.idCompany')}</label>
            <input
              type="text"
              value={rivhitIdCompany}
              onChange={(e) => setRivhitIdCompany(e.target.value)}
              placeholder={rivhitHasCredentials ? '••••••••' : t('settings.rivhit.idCompanyPlaceholder')}
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
            />
          </div>

          {rivhitHasCredentials && !rivhitUsername && !rivhitPassword && !rivhitIdCompany && (
            <span className="text-xs text-green-600">✓ {t('settings.rivhit.credentialsSet')}</span>
          )}

          <p className="text-xs text-gray-400">🔒 {t('settings.rivhit.credentialsHint')}</p>

          {/* API Token (advanced) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.rivhit.apiToken')}</label>
            <div className="relative">
              <input
                type="password"
                value={rivhitToken}
                onChange={(e) => setRivhitToken(e.target.value)}
                placeholder={rivhitHasToken ? '••••••••••••••••' : t('settings.rivhit.apiTokenPlaceholder')}
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
              />
              {rivhitHasToken && !rivhitToken && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-green-600">
                  ✓ {t('settings.rivhit.tokenSet')}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-1">🔒 {t('settings.rivhit.apiTokenHint')}</p>
          </div>
        </div>

        {/* Save + feedback */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleRivhitTest}
            disabled={rivhitTesting || rivhitSaving || (!rivhitToken && !rivhitHasToken && !rivhitUsername && !rivhitPassword && !rivhitIdCompany && !rivhitHasCredentials)}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            {rivhitTesting ? t('settings.rivhit.testing') : t('settings.rivhit.test')}
          </button>
          <button
            onClick={handleRivhitSave}
            disabled={rivhitSaving || (!rivhitToken && !rivhitHasToken && !rivhitUsername && !rivhitHasCredentials)}
            className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:opacity-50 transition-colors"
          >
            {rivhitSaving ? t('settings.rivhit.saving') : t('settings.rivhit.save')}
          </button>
          {rivhitSaved && <span className="text-sm text-green-600 font-medium">✓ {t('settings.rivhit.saved')}</span>}
          {rivhitTestResult && (
            <span className={`text-sm font-medium ${rivhitTestResult.success ? 'text-green-600' : 'text-amber-700'}`}>
              {rivhitTestResult.success ? '✓' : '!' } {rivhitTestResult.message}
            </span>
          )}
          {rivhitError && <span className="text-sm text-red-600 font-medium">✕ {rivhitError}</span>}
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('settings.whatsappAssistant.title')}</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {t('settings.whatsappAssistant.description')}
            </p>
          </div>
          <button
            onClick={refreshPhoneLinkStatus}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            {t('common.refresh')}
          </button>
        </div>

        {phoneLinkStatus?.verified && phoneLinkStatus.linked_phone_number && (
          <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
            {t('settings.whatsappAssistant.linkedNumber')}: <span className="font-medium">{phoneLinkStatus.linked_phone_number}</span>
            {phoneLinkStatus.linked_at && <span className="text-green-700"> · {t('settings.whatsappAssistant.verifiedAt')} {new Date(phoneLinkStatus.linked_at).toLocaleString()}</span>}
          </div>
        )}

        {phoneLinkStatus?.pending && phoneLinkStatus.pending_phone_number && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {t('settings.whatsappAssistant.pendingFor')} <span className="font-medium">{phoneLinkStatus.pending_phone_number}</span>
            {phoneLinkStatus.pending_expires_at && (
              <span> · {t('settings.whatsappAssistant.expiresAt')} {new Date(phoneLinkStatus.pending_expires_at).toLocaleString()}</span>
            )}
          </div>
        )}

        <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.whatsappAssistant.phoneNumber')}</label>
            <input
              type="text"
              value={phoneNumberInput}
              onChange={(e) => setPhoneNumberInput(e.target.value)}
              placeholder="+972501234567"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
            />
            <p className="text-xs text-gray-400 mt-1">{t('settings.whatsappAssistant.phoneHint')}</p>
          </div>
          <button
            onClick={handleStartPhoneLink}
            disabled={phoneLinkLoading || !phoneNumberInput.trim()}
            className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {phoneLinkLoading ? t('settings.whatsappAssistant.sending') : t('settings.whatsappAssistant.sendVerification')}
          </button>
        </div>

        <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.whatsappAssistant.verificationCode')}</label>
            <input
              type="text"
              value={phoneCodeInput}
              onChange={(e) => setPhoneCodeInput(e.target.value)}
              placeholder={t('settings.whatsappAssistant.codePlaceholder')}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
            />
            <p className="text-xs text-gray-400 mt-1">{t('settings.whatsappAssistant.codeHint')}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleConfirmPhoneLink}
              disabled={phoneLinkLoading || !phoneNumberInput.trim() || !phoneCodeInput.trim()}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {t('settings.whatsappAssistant.verifyCode')}
            </button>
            <button
              onClick={handleDeletePhoneLink}
              disabled={phoneLinkLoading || (!phoneLinkStatus?.verified && !phoneLinkStatus?.pending)}
              className="rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
            >
              {t('settings.whatsappAssistant.unlink')}
            </button>
          </div>
        </div>

        {phoneLinkMessage && <p className="text-sm font-medium text-green-700">✓ {phoneLinkMessage}</p>}
        {phoneLinkError && <p className="text-sm font-medium text-red-600">✕ {phoneLinkError}</p>}
      </section>

      {/* WhatsApp Connection */}
      <section id="whatsapp-connection" className="rounded-lg border border-gray-200 bg-white p-5 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">{t('whatsapp.title')}</h2>
        <WhatsAppConnect compact onStatusChange={(s) => setWaConnected(s === 'connected')} />
      </section>

      {/* Chat Whitelist Management */}
      {waConnected ? (
      <section className="rounded-lg border border-gray-200 bg-white p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('settings.chatManagement')}</h2>
            <p className="text-xs text-gray-500 mt-0.5">Choose which WhatsApp groups to monitor</p>
          </div>
          <button
            onClick={handleDiscover}
            disabled={discovering}
            className="rounded-md bg-[#25D366] px-4 py-2 text-sm font-medium text-white hover:bg-[#1da851] disabled:opacity-50 transition-colors flex items-center gap-2"
          >
            {discovering ? (
              <>
                <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Scanning…
              </>
            ) : (
              <>🔍 Discover Groups</>
            )}
          </button>
        </div>

        {/* Discover results */}
        {discoverResults && (
          <div className="rounded-md border border-blue-200 bg-blue-50 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-800">
                Found {discoverResults.length} WhatsApp groups
              </span>
              <button onClick={() => setDiscoverResults(null)} className="text-xs text-blue-600 hover:underline">
                Close
              </button>
            </div>
            <div className="max-h-60 overflow-y-auto space-y-1">
              {discoverResults
                .sort((a, b) => (b.is_monitored ? 1 : 0) - (a.is_monitored ? 1 : 0) || a.name.localeCompare(b.name))
                .map((g) => (
                <div key={g.id} className="flex items-center justify-between rounded px-2 py-1.5 bg-white border border-blue-100">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm">{g.chat_type === 'group' ? '👥' : '👤'}</span>
                    <span className="text-sm font-medium text-gray-900 truncate">{g.name}</span>
                    {g.participants_count && g.participants_count > 1 && (
                      <span className="text-[10px] text-gray-400">({g.participants_count})</span>
                    )}
                  </div>
                  <button
                    onClick={() => handleChatUpdate(g.id, { is_monitored: !g.is_monitored })}
                    className={`shrink-0 rounded-full px-3 py-0.5 text-xs font-medium transition-colors ${
                      g.is_monitored
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    {g.is_monitored ? '✓ Monitored' : 'Not monitored'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabs: Groups / Contacts */}
        <div className="flex border-b border-gray-200">
          {(['group', 'private'] as const).map((tab) => {
            const count = chats.filter(c => c.chat_type === tab).length;
            const monitored = chats.filter(c => c.chat_type === tab && c.is_monitored).length;
            return (
              <button
                key={tab}
                onClick={() => { setChatTab(tab); setChatSearch(''); tab === 'group' ? setGroupPage(1) : setContactPage(1); }}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  chatTab === tab
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab === 'group' ? '👥 Groups' : '👤 Contacts'}
                <span className="ml-1.5 text-xs text-gray-400">({monitored}/{count})</span>
              </button>
            );
          })}
        </div>

        {/* Search + Paginated List */}
        <ChatListWithPagination
          chats={chats}
          chatType={chatTab}
          search={chatSearch}
          onSearchChange={(v) => { setChatSearch(v); chatTab === 'group' ? setGroupPage(1) : setContactPage(1); }}
          page={chatTab === 'group' ? groupPage : contactPage}
          onPageChange={chatTab === 'group' ? setGroupPage : setContactPage}
          pageSize={PAGE_SIZE}
          onChatUpdate={handleChatUpdate}
          onRunAnalysis={handleRunAnalysis}
          runningAnalysisChatId={runningAnalysisChatId}
          t={t}
        />
      </section>
      ) : (
      <section className="rounded-lg border border-gray-200 bg-white p-5 space-y-2 opacity-60">
        <h2 className="text-lg font-semibold text-gray-900">{t('settings.chatManagement')}</h2>
        <p className="text-sm text-gray-500">Connect WhatsApp above to manage monitored groups and contacts.</p>
      </section>
      )}

      {/* Sticky save button */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200 p-4">
        <div className="max-w-3xl mx-auto flex items-center justify-end gap-3">
          {saved && <span className="text-sm text-green-600 font-medium">✓ {t('settings.saved')}</span>}
          <button
            onClick={handleSave}
            disabled={saving}
            className="rounded-md bg-primary-600 px-6 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:opacity-50 transition-colors"
          >
            {saving ? t('common.loading') : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
